import { AlertCircle, CheckCircle2, ImageIcon, Loader2, RefreshCw } from 'lucide-react';
import { formatCourierPackageTag, mapCourierError, useTranslation } from '@dukanti/shared';
import type {
  AdminCourierProof,
  CourierOrderAssignmentSummary,
  CourierProofStatus,
  TranslationKey,
} from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAdminCourierProof } from '@/hooks/use-admin-courier-proof';
import { useConfirmCourierDeliveryProof } from '@/hooks/use-confirm-courier-delivery-proof';

export interface CourierProofReviewCardProps {
  readonly storeId: string;
  readonly assignment: CourierOrderAssignmentSummary | null;
  readonly proof?: AdminCourierProof | null;
}

interface CourierProofPreviewProps {
  readonly storeId: string;
  readonly proofId: string;
  readonly proof?: AdminCourierProof | null;
  readonly fallbackSubmittedAt: string | null;
  readonly isConfirmed: boolean;
}

function proofStatusKey(status: CourierProofStatus | undefined): TranslationKey {
  if (status === 'internally_confirmed') {
    return 'admin.orders.courierProof.confirmed' as TranslationKey;
  }
  if (status === 'awaiting_internal_confirmation' || status === 'submitted') {
    return 'admin.orders.courierProof.awaitingConfirmation' as TranslationKey;
  }
  return 'admin.orders.courierProof.awaitingProof' as TranslationKey;
}

function CourierProofPreview({
  storeId,
  proofId,
  proof,
  fallbackSubmittedAt,
  isConfirmed,
}: CourierProofPreviewProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const proofQuery = useAdminCourierProof(storeId || null, proofId);
  const confirmProof = useConfirmCourierDeliveryProof(storeId);
  const activeProof = proofQuery.data ?? proof ?? null;
  const submittedAt = activeProof?.submitted_at ?? fallbackSubmittedAt;
  const submittedText = submittedAt
    ? new Intl.DateTimeFormat(lang, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(submittedAt))
    : null;

  return (
    <>
      {proofQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-start text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{t('common.loading')}</span>
        </div>
      ) : null}

      {proofQuery.isError ? (
        <div
          role="alert"
          className="border-destructive/25 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{t(mapCourierError(proofQuery.error) as TranslationKey)}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => void proofQuery.refetch()}>
            <RefreshCw className="size-4" aria-hidden />
            <span>{t('common.retry')}</span>
          </Button>
        </div>
      ) : null}

      {activeProof?.signed_photo_url ? (
        <figure
          dir={dir}
          className="border-border bg-admin-surface-muted overflow-hidden rounded-lg border"
        >
          <img
            src={activeProof.signed_photo_url}
            alt={t('admin.orders.courierProof.viewProof' as TranslationKey)}
            className="max-h-80 w-full object-contain"
          />
        </figure>
      ) : !proofQuery.isLoading ? (
        <div className="border-border bg-admin-surface-muted text-muted-foreground flex items-center gap-2 rounded-lg border p-3 text-start text-sm">
          <ImageIcon className="size-4 shrink-0" aria-hidden />
          <span>{t('admin.orders.courierProof.errors.signedUrlFailed' as TranslationKey)}</span>
        </div>
      ) : null}

      {submittedText ? (
        <p className="text-muted-foreground text-start text-xs">{submittedText}</p>
      ) : null}

      <Button
        type="button"
        disabled={isConfirmed || confirmProof.isPending}
        onClick={() => confirmProof.mutate(proofId)}
      >
        {confirmProof.isPending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <CheckCircle2 className="size-4" aria-hidden />
        )}
        <span>{t('admin.orders.courierProof.confirm' as TranslationKey)}</span>
      </Button>
    </>
  );
}

export function CourierProofReviewCard({
  storeId,
  assignment,
  proof,
}: CourierProofReviewCardProps): React.JSX.Element | null {
  const { t, dir } = useTranslation();
  const proofId = proof?.id ?? assignment?.latest_proof_id ?? null;
  const rawProofStatus = assignment?.proof_status ?? (proof ? 'submitted' : 'none');
  const isConfirmed = rawProofStatus === 'internally_confirmed' || !!proof?.admin_confirmed_at;
  const proofStatus: CourierProofStatus = isConfirmed ? 'internally_confirmed' : rawProofStatus;
  const packageTag = proof?.package_tag ?? assignment?.package_tag ?? null;

  if (!assignment && !proof) return null;

  return (
    <section
      dir={dir}
      data-testid="courier-proof-review-card"
      className="border-border bg-card shadow-admin-card flex flex-col gap-4 rounded-lg border p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-foreground text-start text-lg font-semibold">
            {t('admin.orders.courierProof.viewProof' as TranslationKey)}
          </h2>
          {packageTag ? (
            <p className="text-muted-foreground mt-1 text-start text-sm">
              {t('admin.orders.courierAssignment.packageTag' as TranslationKey, {
                tag: formatCourierPackageTag(packageTag),
              })}
            </p>
          ) : null}
        </div>
        <Badge variant={isConfirmed ? 'success' : proofId ? 'warning' : 'neutral'}>
          {t(proofStatusKey(proofStatus))}
        </Badge>
      </div>

      {!proofId ? (
        <div className="border-honey/40 bg-honey/10 text-foreground rounded-lg border p-3 text-start text-sm">
          {t('admin.orders.courierProof.awaitingProof' as TranslationKey)}
        </div>
      ) : null}

      {proofId ? (
        <CourierProofPreview
          storeId={storeId}
          proofId={proofId}
          proof={proof}
          fallbackSubmittedAt={assignment?.latest_proof_submitted_at ?? null}
          isConfirmed={isConfirmed}
        />
      ) : null}
    </section>
  );
}
